import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';

const writeSearchParams = (pathname: string, q: Record<string, string>) => {
  localStorage.setItem('page-params-' + pathname, JSON.stringify(q));
};

const readSearchParams = (pathname: string): Record<string, string> => {
  const json = localStorage.getItem('page-params-' + pathname);
  if (!json) return {};
  try {
    return JSON.parse(json) ?? {};
  } catch (e) {
    console.error(e);
    return {};
  }
};

const stringifySearchParams = (q: Record<string, string>) =>
  Object.entries(q)
    .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
    .join('&');

const isEqParams = (
  q1: Record<string, string>,
  q2: Record<string, string>
): boolean => {
  const keysQ1 = Object.keys(q1).sort();
  const keysQ2 = Object.keys(q2).sort();
  if (keysQ1.length !== keysQ2.length) return false;
  for (let i = 0; i < keysQ1.length; i++) {
    const q1Key = keysQ1[i];
    const q2Key = keysQ2[i];
    const q1Value = q1[q1Key];
    const q2Value = q2[q2Key];
    if (q1Value !== q2Value) {
      return false;
    }
  }
  return true;
};

export function useQueryParams(): Record<string, string> {
  const location = useLocation();

  const queryParams = useMemo(() => {
    if (!location.search) {
      return {};
    }
    return location.search
      .substr(1)
      .split('&')
      .reduce((acc, _item) => {
        const item = _item.split('=');
        acc[item[0]] = decodeURIComponent(item[1]);

        return acc;
      }, {} as Record<string, string>);
  }, [location.search]);

  return queryParams;
}

function useSearchParams(): Record<string, string> {
  const location = useLocation();
  const navigate = useNavigate();
  const page = location.pathname;

  const searchParams = useMemo(() => {
    if (!location.search) {
      return {};
    }
    return location.search
      .substr(1)
      .split('&')
      .reduce((acc, _item) => {
        const item = _item.split('=');
        acc[item[0]] = decodeURIComponent(item[1]);

        return acc;
      }, {} as Record<string, string>);
  }, [location.search]);

  const initialParams = useMemo(() => readSearchParams(page), [page]);

  const mergedParams = useMemo(
    () => ({ ...initialParams, ...searchParams }),
    [initialParams, searchParams]
  );

  useEffect(() => {
    if (!isEqParams(initialParams, searchParams)) {
      navigate(`${page}?${stringifySearchParams(mergedParams)}`);
    }
  }, [page]);

  useEffect(() => {
    if (!isEqParams(initialParams, searchParams)) {
      writeSearchParams(page, mergedParams);
    }
  }, [page, initialParams, searchParams]);

  return mergedParams;
}

export default useSearchParams;
